import { useQuery } from '@tanstack/react-query';
import { useEffect, type JSX } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import {
  millisUntilSignedUrlRefresh,
  publicBookApi,
  type BookPreviewDto,
} from '../api/book-preview.js';
import { BookReader } from '../books/BookReader.js';
import { useBrand } from '../brand/context.js';
import { BrandLogo } from '../components/BrandLogo.js';
import { useI18n } from '../i18n/context.js';

/**
 * Чтение книги по публичной ссылке `/p/:token` (§3.16).
 *
 * Открыта гостю: ссылку отправляют бабушке, и вход ей не нужен. Читалка —
 * та же, что у владельца в «Просмотре», но ничего изменить нельзя: правки
 * запрещены на уровне апи, пока книга опубликована.
 *
 * The footer carries the abuse address (abuse-response.md §3.5). It is the only page a
 * stranger sees, so it is the only place where a report can begin.
 */
export function PublicBookPage(): JSX.Element {
  const { token = '' } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useI18n();
  const fromLanding = searchParams.get('from') === 'landing';
  // Opened from inside the app, by the onboarding's example link: "back" returns there.
  // Only that link sets the flag, so a shared link and the landing keep their buttons.
  const fromApp = (useLocation().state as { fromApp?: boolean } | null)?.fromApp === true;
  const navigate = useNavigate();
  useNoIndex();
  const query = useQuery({
    queryKey: ['public-book', token],
    queryFn: () => publicBookApi.get(token),
    enabled: Boolean(token),
    // Media-ссылки живут час, а книгу могут читать вслух дольше.
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: true,
    refetchInterval: (current) => {
      const data = current.state.data;
      if (!data) return false;
      return millisUntilSignedUrlRefresh(data) ?? false;
    },
  });
  usePublicBookMetadata(query.data ?? null, token, t);

  if (query.isPending) {
    return (
      <main className="grid min-h-screen place-items-center">
        <p role="status">{t.publicBook.loading}</p>
      </main>
    );
  }

  if (!query.data) {
    return (
      <main className="grid min-h-screen place-items-center p-6 text-center">
        <div className="max-w-prose">
          <h1 className="text-2xl font-semibold">{t.publicBook.notFoundTitle}</h1>
          <p role="alert" className="mt-2 text-sm text-[var(--kitup-color-muted)]">
            {t.publicBook.notFoundText}
          </p>
          <Link to="/" className="kitup-secondary-button mt-5">
            {t.publicBook.home}
          </Link>
        </div>
      </main>
    );
  }

  const readerBook: BookPreviewDto = {
    ...query.data,
    bookId: stableUuid(token),
    scenes: query.data.scenes.map((scene) => ({
      ...scene,
      id: stableUuid(`${token}:${scene.index}`),
    })),
  };

  return (
    <>
      {/*
        The page a family forwards is the one strangers see, and it carried no brand at all:
        the only words on it besides the book were the abuse line. The bar says whose book
        this is and offers the next reader a way to make one.
      */}
      <header className="kitup-public-bar">
        <Link to="/" aria-label={t.app.name} className="inline-flex no-underline">
          <BrandLogo />
        </Link>
        {fromApp ? (
          <button type="button" className="kitup-secondary-button" onClick={() => navigate(-1)}>
            {`← ${t.common.back}`}
          </button>
        ) : (
          <Link to={fromLanding ? '/' : '/register'} className="kitup-secondary-button">
            {fromLanding ? `← ${t.publicBook.backToLanding}` : t.publicBook.makeYourOwn}
          </Link>
        )}
      </header>
      <main className="kitup-preview-page kitup-preview-page-public">
        <BookReader book={readerBook} />
      </main>
      <AbuseFooter />
    </>
  );
}

/**
 * Address and process link, outside `kitup-preview-page` on purpose: that surface is a
 * viewport-sized reader with `overflow: hidden` on small screens, and a link inside it would
 * be clipped exactly where a person who needs it is looking.
 *
 * Renders nothing when the deployment has no abuse address: a link to a mailbox that does not
 * exist is worse than no link, because it looks like a channel and is not one.
 */
function AbuseFooter(): JSX.Element | null {
  const { t } = useI18n();
  const { abuse } = useBrand();

  if (!abuse.email) return null;

  return (
    <footer className="border-t border-slate-200 bg-white/70 px-4 py-3 text-center text-xs text-[var(--kitup-color-muted)]">
      <p>
        {t.publicBook.reportLabel}{' '}
        <a className="underline" href={`mailto:${abuse.email}`}>
          {abuse.email}
        </a>
        {' · '}
        <Link className="underline" to="/report">
          {t.publicBook.reportLink}
        </Link>
      </p>
    </footer>
  );
}

/** Локальный UUID для читалки: наружу внутренние id книги и сцен не выдаются. */
function stableUuid(value: string): string {
  let hex = '';
  for (let salt = 0; salt < 4; salt += 1) {
    let hash = (0x811c9dc5 ^ salt) >>> 0;
    for (const char of value) {
      hash ^= char.codePointAt(0) ?? 0;
      hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    hex += hash.toString(16).padStart(8, '0');
  }
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-4${hex.slice(13, 16)}-8${hex.slice(17, 20)}-${hex.slice(20, 32)}`;
}

/**
 * Книга с детскими фотографиями не должна попасть в поисковый индекс, даже если
 * ссылку куда-нибудь вставили. Тег ставится только на этой странице: `index.html`
 * общий, и запрет там снял бы из поиска лендинг.
 */
function useNoIndex(): void {
  useEffect(() => {
    const meta =
      document.querySelector<HTMLMetaElement>('meta[name="robots"]') ??
      document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow, noarchive';
    if (!meta.isConnected) document.head.append(meta);
    return () => meta.remove();
  }, []);
}

function usePublicBookMetadata(
  book: BookPreviewDto | Omit<BookPreviewDto, 'bookId' | 'scenes'> | null,
  token: string,
  t: ReturnType<typeof useI18n>['t'],
): void {
  useEffect(() => {
    if (!book) return;

    const title = `${book.title} — ${t.app.name}`;
    const description = book.subtitle || book.dedication || `${book.title}. ${t.meta.description}`;
    const image = book.cover?.url ?? new URL('/og.png', window.location.origin).toString();
    const pageUrl = new URL(`/p/${encodeURIComponent(token)}`, window.location.origin).toString();
    const imageAlt = t.preview.coverAlt(book.title);

    document.title = title;
    setMeta('name', 'description', description);
    setMeta('property', 'og:type', 'book');
    setMeta('property', 'og:site_name', t.app.name);
    setMeta('property', 'og:url', pageUrl);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:image', image);
    setMeta('property', 'og:image:secure_url', image);
    setMeta('property', 'og:image:alt', imageAlt);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', image);
    setMeta('name', 'twitter:image:alt', imageAlt);
    setCanonical(pageUrl);

    return () => {
      const genericImage = new URL('/og.png', window.location.origin).toString();
      document.title = t.meta.title;
      setMeta('name', 'description', t.meta.description);
      setMeta('property', 'og:type', 'website');
      setMeta('property', 'og:site_name', t.app.name);
      removeMeta('property', 'og:url');
      setMeta('property', 'og:title', t.meta.title);
      setMeta('property', 'og:description', t.meta.description);
      setMeta('property', 'og:image', genericImage);
      removeMeta('property', 'og:image:secure_url');
      setMeta('property', 'og:image:alt', t.meta.ogImageAlt);
      setMeta('name', 'twitter:card', 'summary_large_image');
      removeMeta('name', 'twitter:title');
      removeMeta('name', 'twitter:description');
      removeMeta('name', 'twitter:image');
      removeMeta('name', 'twitter:image:alt');
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, [book, t, token]);
}

function setMeta(attribute: 'name' | 'property', key: string, content: string): void {
  let meta = document.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, key);
    document.head.append(meta);
  }
  meta.content = content;
}

function removeMeta(attribute: 'name' | 'property', key: string): void {
  document.querySelector(`meta[${attribute}="${key}"]`)?.remove();
}

function setCanonical(href: string): void {
  let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.append(link);
  }
  link.href = href;
}
